import styled from '@emotion/styled';
import { useMemo } from 'react';

const RangeWrapper = styled.div<{ value: number; max: number }>`
    width: 100%;

    &::after {
        content: '';
        width: 12px;
        height: 12px;
        background: ${({ theme }) => theme.colors.secondary[900]};
        cursor: pointer;
        border-radius: 50%;
        left: ${({ value, max }) => `calc(${(value / max) * 100}% - 6px)`};
        top: 4px;
        position: absolute;
        z-index: 1;
        transition: left 0.3s linear;
    }
`;

const Range = styled.input<{ value: number; max: number }>`
    width: 100%;
    margin: 0;
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    background: ${({ theme }) => theme.colors.black[50]};
    outline: none;
    border-radius: 12px;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${({ value, max }) => (value / max) * 100}%;
        background: ${({ theme }) => theme.colors.secondary[900]};
        border-radius: 12px;
        transition: width 0.3s linear;
    }

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        display: none;
    }

    &::-moz-range-thumb {
        display: none;
        background-color: transparent;
        border: none;
    }

    &::-webkit-slider-runnable-track {
        width: 100%;
        height: 4px;
        cursor: pointer;
        background: transparent;
        border-radius: 12px;
        position: relative;
    }

    &::-moz-range-track {
        width: 100%;
        height: 4px;
        cursor: pointer;
        background: transparent;
        border-radius: 12px;
        position: relative;
    }
`;

const Container = styled.div`
    display: flex;
    justify-content: space-between;
    width: calc(100% + 120px);
    flex-wrap: nowrap;
`;

const TabLabel = styled.p<{ active: boolean; disabled?: boolean }>`
    color: ${({ theme, active }) => (active ? theme.colors.secondary[900] : theme.colors.black[600])};
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    width: 120px;
    text-align: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export interface ProgressTab {
    id: number;
    label: string;
    disabled?: boolean;
    hidden?: boolean;
}

interface Props {
    tab: number;
    setTab: (tab: number) => void;
    tabs: ProgressTab[];
    className?: string;
}

export const ProgressTabs = ({ tab, setTab, tabs, className }: Props) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = tabs.find(t => t.id === +e.target.value);
        if (!selected || selected.disabled) {
            return;
        }

        setTab(+e.target.value);
    };

    const visibleTabs = useMemo(() => tabs.filter(({ hidden }) => !hidden), [tabs]);
    const tabIndex = useMemo(() => visibleTabs.findLastIndex(t => t.id <= tab) || 0, [visibleTabs, tab]);
    const maxRange = visibleTabs.length - 1;

    return (
        <div className={className}>
            <RangeWrapper value={tabIndex} max={maxRange}>
                <Range type="range" value={tabIndex} step={1} min={0} max={maxRange} onChange={handleChange} />
            </RangeWrapper>
            <Container>
                {visibleTabs.map(t => (
                    <TabLabel
                        key={t.id}
                        onClick={() => (t.disabled ? undefined : setTab(t.id))}
                        active={tabIndex === t.id}
                        disabled={t.disabled}>
                        {t.label}
                    </TabLabel>
                ))}
            </Container>
        </div>
    );
};
